import { Visibility, VisibilityOff } from "@mui/icons-material";
import { Box, Button, FormControlLabel, Grid2, IconButton, InputAdornment, Paper, Switch, TextField, Typography } from "@mui/material";
import { useTheme } from '@mui/material/styles';
import { Formik } from "formik";
import { enqueueSnackbar } from "notistack";
import React from "react";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router";
import { isTaxID } from "validator";
import * as Yup from "yup";
import { createAccount } from "../../api/account";
import { useTranslationHook } from "../../hooks";
import { useFormStyles } from "../../hooks/useFormStyles";
import { setLoading } from "../../store/UI";
import { PrivateSettings } from "../../types/interfaces/app";
import { CreateAccountRequest } from "../../types/model/account";
import { StoreInterfaces } from "../../types/store/StoreInterfaces";
import { getRoleIdFromType } from "../../utils/App";

const AddAccount = () => {
  const { t } = useTranslationHook();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const theme = useTheme();
  const [showPassword, setShowPassword] = React.useState(false);
  const [showConfirm, setShowConfirm] = React.useState(false);

  const privateSettings: PrivateSettings | undefined = useSelector(
    (state: StoreInterfaces) => state.app.privateSettings
  );
  const formStyles = useFormStyles();

  const handleCancelAction = () => {
    navigate(-1);
  };

  const handleSaveAction = (values: any) => {
    dispatch(setLoading(true));
    const request: CreateAccountRequest = {
      name: values.fieldA1,
      address: values.fieldA3,
      city: values.fieldA6,
      email: values.fieldA2,
      fiscalNumber: values.fieldA4,
      phoneNumber: values.fieldA7,
      zipCode: values.fieldA5,
      allowExternalManagers: values.fieldAllowExternalManagers,
      maxInternalManagers: values.fieldLimitInternalManagers
        ? (values.fieldMaxInternalManagers === '' 
            ? null 
            : Number(values.fieldMaxInternalManagers))
        : null,
      user: {
        roleId: getRoleIdFromType("ACCOUNT_USER", privateSettings?.roles) || 0,
        email: values.fieldU2,
        name: values.fieldU1,
        password: values.fieldU3,
        phoneNumber: values.fieldU5,
      },
    };
    createAccount(request)
      .then((response) => {
        enqueueSnackbar(t("admin.accounts.created"), {
          variant: "success",
        });
        navigate(`/accounts/${response.id}`);
      })
      .catch((error) => {
        if (
          error?.response?.data?.status === -1
        ) {
          enqueueSnackbar(t("back-end." + error.response.data.label), {
            variant: "error",
          });
        }
      })
      .finally(() => {
        dispatch(setLoading(false));
      });
  };

  return (
    <Box sx={{ width: "100%" }}>
      <Paper sx={{ width: "100%", mb: 2, p: 2 }}>
        <Typography sx={{ mb: 2 }} variant="h4">
          {t("accounts.table.add-account")}
        </Typography>

        <Formik
          initialValues={{
            fieldA1: "", //Name
            fieldA2: "", //email
            fieldA3: "", //Address
            fieldA4: "", //Fiscal
            fieldA5: "", //Zip
            fieldA6: "", //City
            fieldA7: "", //Phone
            fieldAllowExternalManagers: false,
            fieldMaxInternalManagers: '',
            fieldLimitInternalManagers: false,
            fieldU1: "", //User name
            fieldU2: "", //User email
            fieldU3: "", //User Password
            fieldU4: "", //User Password Confirmation
            fieldU5: "", //User phone
          }}
          onSubmit={(values) => {
            handleSaveAction(values);
          }}
          validationSchema={Yup.object().shape({
            fieldA1: Yup.string().required(
              t("admin.accounts.account-name-required")
            ),
            fieldA2: Yup.string()
              .email(t("admin.accounts.account-email-valid"))
              .required(t("admin.accounts.account-email-required")),
            fieldA3: Yup.string()
              .required(t("admin.accounts.account-address-required")),
            fieldA4: Yup.string()
              .matches(
                /^[1235689]\d{8}$/,
                t("admin.accounts.account-fiscal-number-valid")
              )
              .test("validator-nif", function (value) {
                let isValidTaxID = false;
                if (value) isValidTaxID = isTaxID(value, "pt-PT");
                if (isValidTaxID)
                  {return true;}
                else {return this.createError({
                    path: this.path,
                    message: t("admin.accounts.account-fiscal-number-valid"),
                  });}
              })
              .required(t("admin.accounts.account-fiscal-number-required")),
            fieldA5: Yup.string()
              .matches(/^\d{4}-\d{3}$/, t("admin.accounts.account-zip-valid"))
              .required(t("admin.accounts.account-zip-required")),
            fieldA6: Yup.string()
              .required(t("admin.accounts.account-city-required")),
            fieldA7: Yup.string()
              .matches(/^\+?\d+$/, t("admin.users.user-phone-number-valid"))
              .required(t("admin.accounts.account-phone-number-required")),
            fieldAllowExternalManagers: Yup.boolean(),
            fieldLimitInternalManagers: Yup.boolean(),
            fieldMaxInternalManagers: Yup.number().nullable().when('fieldlimitInternalManagers', (fieldlimitInternalManagers, schema) => {
              return fieldlimitInternalManagers
                ? schema.nullable()
                : schema.required(t('admin.accounts.max-internal-managers-required')).min(0, t('admin.accounts.max-internal-managers-min'));
            }),
            fieldU1: Yup.string().required(t("admin.users.user-name-required")),
            fieldU2: Yup.string().required(t("admin.users.user-email-required")),
            fieldU3: Yup.string().required(
              t("admin.users.user-password-required")
            ),
            fieldU4: Yup.string()
              .required(t("admin.users.user-password-confirmation-required"))
              .oneOf([Yup.ref("fieldU3")], t("admin.users.user-password-match")),
            fieldU5: Yup.string()
              .matches(/^\+?\d+$/, t("admin.users.user-phone-number-valid"))
              .required(t("admin.users.user-phone-number-required")),
          })}
        >
          {(props) => {
            const {
              values,
              touched,
              errors,
              handleChange,
              handleBlur,
              handleSubmit,
              setFieldValue
            } = props;

            return (
              <Box component="form" onSubmit={handleSubmit} sx={{ p: 1 }}>
                {/* ACCOUNT */}
                <Typography variant="h5" sx={{ mb: 2, mt: 1 }}>
                  {t("admin.accounts.account-information")}
                </Typography>

                <Grid2 container spacing={2}>
                  <Grid2 size={6}>
                    <TextField
                      fullWidth
                      id="fieldA1"
                      label={t("admin.accounts.name")}
                      value={values.fieldA1}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={!!(errors.fieldA1 && touched.fieldA1)}
                      helperText={errors.fieldA1 || ""}
                      sx={{ ...formStyles }}
                    />
                  </Grid2>

                  <Grid2 size={6}>
                    <TextField
                      fullWidth
                      id="fieldA4"
                      label={t("admin.accounts.fiscal-number")}
                      value={values.fieldA4}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={!!(errors.fieldA4 && touched.fieldA4)}
                      helperText={errors.fieldA4 || ""}
                      sx={{ ...formStyles }}
                    />
                  </Grid2>

                  <Grid2 size={6}>
                    <TextField
                      fullWidth
                      id="fieldA2"
                      label={t("admin.accounts.email")}
                      value={values.fieldA2}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={!!(errors.fieldA2 && touched.fieldA2)}
                      helperText={errors.fieldA2 || ""}
                      sx={{ ...formStyles }}
                    />
                  </Grid2>

                  <Grid2 size={6}>
                    <Box>
                      <PhoneInput
                        containerClass="my-phone-input"
                        country="pt"
                        value={values.fieldA7}
                        onChange={(phone) =>
                          setFieldValue("fieldA7", phone.startsWith("+") ? phone : `+${phone}`)
                        }
                        onBlur={() => setFieldValue("fieldA7", values.fieldA7, true)}
                        inputStyle={{
                          width: "100%",
                          height: "54px",
                          borderRadius: 8,
                          backgroundColor: theme.palette.background.paper,
                          color: theme.palette.text.primary,
                          border: `1px solid ${
                            errors.fieldA7 && touched.fieldA7
                              ? theme.palette.error.main
                              : theme.palette.divider
                          }`,
                          paddingLeft: "48px",
                          boxSizing: "border-box",
                        }}
                        dropdownStyle={{
                          backgroundColor: theme.palette.background.paper,
                          color: theme.palette.text.primary,
                          borderRadius: 8,
                          border: `1px solid ${theme.palette.divider}`,
                          maxHeight: "200px",
                          overflowY: "auto",
                        }}
                      />

                      {errors.fieldA7 && touched.fieldA7 && (
                        <Typography
                          color="error"
                          variant="caption"
                          sx={{ ml: 1.75, mt: 0.5, display: "block" }}
                        >
                          {errors.fieldA7}
                        </Typography>
                      )}
                    </Box>
                  </Grid2>

                  <Grid2 size={6}>
                    <TextField
                      fullWidth
                      id="fieldA3"
                      label={t("admin.accounts.address")}
                      value={values.fieldA3}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={!!(errors.fieldA3 && touched.fieldA3)}
                      helperText={errors.fieldA3 || ""}
                      sx={{ ...formStyles }}
                    />
                  </Grid2>

                  <Grid2 size={4}>
                    <TextField
                      fullWidth
                      id="fieldA6"
                      label={t("admin.accounts.city")}
                      value={values.fieldA6}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={!!(errors.fieldA6 && touched.fieldA6)}
                      helperText={errors.fieldA6 || ""}
                      sx={{ ...formStyles }}
                    />
                  </Grid2>

                  <Grid2 size={2}>
                    <TextField
                      fullWidth
                      id="fieldA5"
                      label={t("admin.accounts.zip-code")}
                      value={values.fieldA5}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={!!(errors.fieldA5 && touched.fieldA5)}
                      helperText={errors.fieldA5 || ""}
                      sx={{ ...formStyles }}
                    />
                  </Grid2>

                  <Grid2 size={12}>
                    <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 2 }}>
                      <Typography variant="subtitle2" color="text.secondary" sx={{ fontWeight: 600, mb: 2 }}>
                        {t("admin.accounts.managers-settings")}
                      </Typography>

                      <Grid2 container spacing={2} alignItems="center">
                        <Grid2 size={{ xs: 12, sm: 4 }}>
                          <FormControlLabel
                            control={
                              <Switch
                                id="fieldAllowExternalManagers"
                                checked={values.fieldAllowExternalManagers}
                                onChange={handleChange}
                              />
                            }
                            label={t("admin.accounts.allow-external-managers")}
                          />
                        </Grid2>

                        <Grid2 size={{ xs: 12, sm: 8 }}>
                          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                            <FormControlLabel
                              control={
                                <Switch
                                  id="fieldLimitInternalManagers"
                                  checked={values.fieldLimitInternalManagers}
                                  onChange={(event) => {
                                    setFieldValue("fieldLimitInternalManagers", event.target.checked);
                                    if (!event.target.checked) setFieldValue('fieldMaxInternalManagers', '');
                                  }}
                                />
                              }
                              label={t("admin.accounts.limit-internal-managers")}
                              sx={{ mr: 0, flexShrink: 0 }}
                            />
                            <TextField
                              id="fieldMaxInternalManagers"
                              label={t("admin.accounts.max-internal-managers")}
                              type="number"
                              size="small"
                              disabled={!values.fieldLimitInternalManagers}
                              value={values.fieldMaxInternalManagers}
                              onChange={(event) => {
                                let value = event.target.value;
                                if (Number.parseInt(value) < 0) value = "0";
                                setFieldValue("fieldMaxInternalManagers", value);
                              }}
                              onBlur={handleBlur}
                              error={!!(errors.fieldMaxInternalManagers && touched.fieldMaxInternalManagers)}
                              helperText={errors.fieldMaxInternalManagers || ""}
                              sx={{ width: 275 }}
                            />
                          </Box>
                        </Grid2>
                      </Grid2>
                    </Paper>
                  </Grid2>
                </Grid2>

                {/* USER */}
                <Typography variant="h5" sx={{ mb: 2, mt: 4 }}>
                  {t("admin.users.user-information")}
                </Typography>

                <Grid2 container spacing={2}>
                  <Grid2 size={6}>
                    <TextField
                      fullWidth
                      id="fieldU1"
                      label={t("admin.users.name")}
                      value={values.fieldU1}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={!!(errors.fieldU1 && touched.fieldU1)}
                      helperText={errors.fieldU1 || ""}
                      sx={{ ...formStyles }}
                    />
                  </Grid2>

                  <Grid2 size={6}>
                    <TextField
                      fullWidth
                      id="fieldU2"
                      label={t("admin.users.email")}
                      value={values.fieldU2}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={!!(errors.fieldU2 && touched.fieldU2)}
                      helperText={errors.fieldU2 || ""}
                      sx={{ ...formStyles }}
                    />
                  </Grid2>

                  <Grid2 size={6}>
                    <Box>
                      <PhoneInput
                        containerClass="my-phone-input"
                        country="pt"
                        value={values.fieldU5}
                        onChange={(phone) =>
                          setFieldValue("fieldU5", phone.startsWith("+") ? phone : `+${phone}`)
                        }
                        onBlur={() => setFieldValue("fieldU5", values.fieldU5, true)}
                        inputStyle={{
                          width: "100%",
                          height: "54px",
                          borderRadius: 8,
                          backgroundColor: theme.palette.background.paper,
                          color: theme.palette.text.primary,
                          border: `1px solid ${
                            errors.fieldU5 && touched.fieldU5
                              ? theme.palette.error.main
                              : theme.palette.divider
                          }`,
                          paddingLeft: "48px",
                          boxSizing: "border-box",
                        }}
                        dropdownStyle={{
                          backgroundColor: theme.palette.background.paper,
                          color: theme.palette.text.primary,
                          borderRadius: 8,
                          border: `1px solid ${theme.palette.divider}`,
                          maxHeight: "200px",
                          overflowY: "auto",
                        }}
                      />

                      {errors.fieldU5 && touched.fieldU5 && (
                        <Typography
                          color="error"
                          variant="caption"
                          sx={{ ml: 1.75, mt: 0.5, display: "block" }}
                        >
                          {errors.fieldU5}
                        </Typography>
                      )}
                    </Box>
                  </Grid2>

                  <Grid2 size={3}>
                    <TextField
                      fullWidth
                      type={showPassword ? "text" : "password"}
                      id="fieldU3"
                      label={t("admin.users.password")}
                      value={values.fieldU3}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={!!(errors.fieldU3 && touched.fieldU3)}
                      helperText={errors.fieldU3 || ""}
                      sx={{ ...formStyles }}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              onClick={() => setShowPassword((prev) => !prev)}
                              edge="end"
                            >
                              {showPassword ? <VisibilityOff /> : <Visibility />}
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Grid2>

                  <Grid2 size={3}>
                    <TextField
                      fullWidth
                      type={showConfirm ? "text" : "password"}
                      id="fieldU4"
                      label={t("admin.users.password-confirm")}
                      value={values.fieldU4}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={!!(errors.fieldU4 && touched.fieldU4)}
                      helperText={errors.fieldU4 || ""}
                      sx={{ ...formStyles }}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              onClick={() => setShowConfirm((prev) => !prev)}
                              edge="end"
                            >
                              {showConfirm ? <VisibilityOff /> : <Visibility />}
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Grid2>
                </Grid2>

                {/* ACTIONS */}
                <Grid2
                  size={12}
                  sx={{
                    display: "flex",
                    justifyContent: "flex-end",
                    gap: 2,
                    mt: 3,
                  }}
                >
                  <Button type="submit" variant="contained">
                    {t("admin.accounts.account-save")}
                  </Button>
                  <Button
                    variant="outlined"
                    color="secondary"
                    onClick={handleCancelAction}
                  >
                    {t("admin.accounts.account-cancel")}
                  </Button>
                </Grid2>
              </Box>
            );
          }}
        </Formik>
      </Paper>
    </Box>
  );
};

export default AddAccount;
